import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Grid, List, ChevronLeft, ChevronRight, Trash2, Users, Plus, X, UserPlus, UserMinus, AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import PageHeader from '../ui/PageHeader';
import DebugPanel from '../ui/DebugPanel';
import { getUserContacts, removeUserContact, setContactRating, clearContactsCache, saveContactsToFile, loadContactsFromFile } from '../../services/userContactsService';
import { getUserGroups, getGroupById, getGroupName, removeMemberFromGroup, deleteGroup } from '../../services/groupsService';
import { PeopleCard } from '../people';
import { StarRating } from '../ui';
import CreateGroupModal from '../dashboard/CreateGroupModal';
import AddMembersModal from '../dashboard/AddMembersModal';
import ConfirmModal from '../ui/ConfirmModal';
import { EditContactModal } from '../contacts';
import { useTranslation } from '../../context/LanguageContext';

/**
 * My Contacts page - shows only user's saved contacts from database
 */
const MyContacts = () => {
    const navigate = useNavigate();
    const { t, language } = useTranslation();
    const [myContacts, setMyContacts] = useState([]);
    const [myGroups, setMyGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('cards');
    const [currentPage, setCurrentPage] = useState(1);
    const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
    const [showAddMembersModal, setShowAddMembersModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(() => {
        // Restore selected group from sessionStorage
        const saved = sessionStorage.getItem('selectedGroupFilter');
        return saved ? JSON.parse(saved) : null;
    });
    const [groupMembers, setGroupMembers] = useState([]);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, contact: null });
    const [deleteGroupModal, setDeleteGroupModal] = useState({ isOpen: false, group: null });
    const [editContactModal, setEditContactModal] = useState({ isOpen: false, contact: null });

    const userEmail = localStorage.getItem('userEmail');
    // Check role from localStorage or from cabaza_user object
    let userRole = localStorage.getItem('userRole') || '';
    let isAdmin = false;
    try {
        const cabazaUser = JSON.parse(localStorage.getItem('cabaza_user') || '{}');
        if (!userRole) {
            userRole = cabazaUser.role || '';
        }
        // Use isAdmin field from cabaza_user (boolean or string or number)
        isAdmin = cabazaUser.isAdmin === true || cabazaUser.isAdmin === 'true' || cabazaUser.isAdmin === 1 || cabazaUser.isAdmin === '1';
        // Debug: log isAdmin value
        console.log('cabaza_user:', cabazaUser);
        console.log('cabaza_user.isAdmin:', cabazaUser.isAdmin, 'type:', typeof cabazaUser.isAdmin, '-> isAdmin:', isAdmin);
    } catch (e) {
        console.error('Error parsing cabaza_user:', e);
    }
    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Debug panel fields configuration
    const debugFields = [
        { key: 'id', label: 'id', color: 'text-cyan-400' },
        { key: 'localuuid', label: 'localuuid', color: 'text-purple-400', format: 'truncate', width: '100px' },
        { key: 'peopleuuid', label: 'peopleuuid', color: 'text-green-400', format: 'truncate', width: '100px' },
        { key: 'contactType', label: 'contactType', format: 'badge' },
        { key: 'firstName', label: 'firstName', color: 'text-white', serverColor: 'text-orange-300' },
        { key: 'lastName', label: 'lastName', color: 'text-white', serverColor: 'text-orange-300' },
        { key: 'email', label: 'email', color: 'text-blue-400', width: '150px' },
        { key: 'phone', label: 'phone', color: 'text-gray-300' },
        { key: 'company', label: 'company', color: 'text-gray-300', width: '100px' },
        { key: 'rating', label: 'rating', color: 'text-yellow-400' },
        { key: 'createdAt', label: 'createdAt', color: 'text-gray-500', format: 'date' }
    ];

    const loadMyContacts = async () => {
        setLoading(true);
        setError(null);
        try {
            // Get contacts directly from API (already filtered by owner)
            const contacts = await getUserContacts(userId, true); // force refresh
            console.log('MyContacts - loaded contacts:', contacts);
            setMyContacts(contacts);

            // Load user's groups from API
            const groups = await getUserGroups(userId);
            console.log('MyContacts - loaded groups:', groups);
            setMyGroups(groups);
        } catch (err) {
            console.error('Error loading contacts:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            loadMyContacts();
        }
    }, [userId]);

    // Listen for contact added event from AddContactModal
    useEffect(() => {
        const handleContactAdded = (event) => {
            const newContact = event.detail;
            if (newContact) {
                setMyContacts(prev => [...prev, newContact]);
            }
        };

        window.addEventListener('contactAdded', handleContactAdded);
        return () => window.removeEventListener('contactAdded', handleContactAdded);
    }, []);

    // Save selected group to sessionStorage when it changes
    useEffect(() => {
        if (selectedGroup) {
            sessionStorage.setItem('selectedGroupFilter', JSON.stringify(selectedGroup));
        } else {
            sessionStorage.removeItem('selectedGroupFilter');
        }
    }, [selectedGroup]);

    // Load group members when component mounts with a saved group filter
    useEffect(() => {
        const loadSavedGroupMembers = async () => {
            if (selectedGroup?.groupUuid) {
                try {
                    const groupDetails = await getGroupById(selectedGroup.groupUuid);
                    if (groupDetails?.members) {
                        setGroupMembers(groupDetails.members);
                    }
                } catch (err) {
                    console.error('Error loading saved group members:', err);
                    // If group no longer exists, clear the filter
                    setSelectedGroup(null);
                    setGroupMembers([]);
                }
            }
        };
        loadSavedGroupMembers();
    }, []);

    const handleRemoveContact = (contactId, e) => {
        e.stopPropagation();
        const contact = myContacts.find(c => c.id === contactId);
        if (contact) {
            setDeleteConfirmModal({ isOpen: true, contact });
        }
    };

    const handleEditContact = (contact, e) => {
        if (e) e.stopPropagation();
        setEditContactModal({ isOpen: true, contact });
    };

    const confirmDeleteContact = async () => {
        if (!deleteConfirmModal.contact) return;
        const success = await removeUserContact(userId, deleteConfirmModal.contact.id);
        if (success) {
            const contactId = deleteConfirmModal.contact.id;
            setMyContacts(prev => prev.filter(c => c.id !== contactId));
        }
        setDeleteConfirmModal({ isOpen: false, contact: null });
    };

    const handleRatingChange = async (contactId, newRating) => {
        // Optimistic update
        setMyContacts(prev => prev.map(c =>
            c.id === contactId ? { ...c, rating: newRating } : c
        ));
        // Save to API
        await setContactRating(contactId, newRating);
    };

    // Filter and sort contacts: rating DESC, then name ASC
    const filteredContacts = useMemo(() => {
        let filtered = myContacts;

        // Apply group filter if a group is selected
        if (selectedGroup && groupMembers.length > 0) {
            // Use contactId from group members to match with contact id
            const memberContactIds = groupMembers.map(m => m.contactId || m.contactid || m.CONTACTID);
            console.log('DEBUG - selectedGroup:', selectedGroup);
            console.log('DEBUG - groupMembers:', groupMembers);
            console.log('DEBUG - memberContactIds:', memberContactIds);
            console.log('DEBUG - myContacts ids:', myContacts.map(c => c.id));
            filtered = filtered.filter(c => memberContactIds.includes(c.id));
            console.log('DEBUG - filtered contacts:', filtered);
        } else if (selectedGroup && groupMembers.length === 0) {
            // Group selected but no members - show empty
            console.log('DEBUG - selectedGroup but no members');
            filtered = [];
        }

        // Apply search filter
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                `${c.firstName} ${c.lastName}`.toLowerCase().includes(search) ||
                c.email?.toLowerCase().includes(search) ||
                c.company?.toLowerCase().includes(search) ||
                c.phone?.includes(search)
            );
        }

        // Sort by rating (DESC) then by name (ASC)
        return [...filtered].sort((a, b) => {
            const ratingA = a.rating || 0;
            const ratingB = b.rating || 0;

            if (ratingB !== ratingA) {
                return ratingB - ratingA;
            }

            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [myContacts, searchTerm, selectedGroup, groupMembers]);

    // Pagination
    const itemsPerPage = viewMode === 'cards' ? 10 : 20;
    const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentContacts = filteredContacts.slice(startIndex, endIndex);

    // Reset to page 1 when search or view mode changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, viewMode]);

    const handleContactClick = (contact) => {
        // Navigate to the appropriate detail page based on contact type
        if (contact.peopleuuid || contact.peopleUuid) {
            // Public contact - navigate to Cabaza profile
            navigate(`/people/${contact.peopleuuid || contact.peopleUuid}`);
        } else {
            // Private contact - navigate to contact detail page
            navigate(`/contact/${contact.localuuid}`);
        }
    };

    // Get card styles based on contactType (PUBLIC = linked to people, PRIVATE = hidden profile, CONTACT = local only)
    const getCardStyles = (contactType, email) => {
        const isCurrentUser = email === userEmail;

        if (isCurrentUser) {
            return {
                container: 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400',
                badge: 'bg-yellow-500 text-white',
                badgeText: 'YOU',
                title: 'text-yellow-700'
            };
        }

        switch (contactType) {
            case 'PUBLIC':
                // Linked to a public people profile
                return {
                    container: 'bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-cabaza-purple',
                    badge: 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white',
                    badgeText: 'PUBLIC',
                    title: 'text-cabaza-purple'
                };
            case 'PRIVATE':
                // Linked profile has PRIVATE visibility
                return {
                    container: 'bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-400',
                    badge: 'bg-orange-500 text-white',
                    badgeText: 'PRIVATE',
                    title: 'text-orange-700'
                };
            case 'CONTACT':
            default:
                // Private contact (not linked to people)
                return {
                    container: 'bg-gray-50 border-2 border-gray-200',
                    badge: 'bg-gray-400 text-white',
                    badgeText: 'CONTACT',
                    title: 'text-gray-700'
                };
        }
    };

    const handleGroupCreated = async (group) => {
        try {
            const groups = await getUserGroups(userId);
            setMyGroups(groups);

            // Auto-select the newly created group
            const newGroup = groups.find(g => g.groupUuid === group.groupUuid) || group;
            setSelectedGroup(newGroup);

            // Load the group members
            const groupDetails = await getGroupById(group.groupUuid);
            if (groupDetails?.members) {
                setGroupMembers(groupDetails.members);
            }
        } catch (err) {
            setMyGroups(prev => [...prev, group]);
            setSelectedGroup(group);
            setGroupMembers([]);
        }
    };

    // Handle group click - select group and load its members
    const handleGroupClick = async (group) => {
        if (selectedGroup?.id === group.id) {
            // Deselect if clicking the same group
            setSelectedGroup(null);
            setGroupMembers([]);
        } else {
            setSelectedGroup(group);
            // Load group members from API
            try {
                const groupDetails = await getGroupById(group.groupUuid);
                if (groupDetails?.members) {
                    setGroupMembers(groupDetails.members);
                }
            } catch (err) {
                console.error('Error loading group members:', err);
                setGroupMembers([]);
            }
        }
    };

    // Clear group filter
    const clearGroupFilter = () => {
        setSelectedGroup(null);
        setGroupMembers([]);
    };

    // Handle add members to group
    const handleAddMembersClick = () => {
        if (selectedGroup) {
            setShowAddMembersModal(true);
        }
    };

    // Handle delete group - open confirmation modal
    const handleDeleteGroup = (group, e) => {
        e.stopPropagation();
        setDeleteGroupModal({ isOpen: true, group });
    };

    // Confirm delete group
    const confirmDeleteGroup = async () => {
        if (!deleteGroupModal.group) return;

        try {
            const success = await deleteGroup(deleteGroupModal.group.groupUuid);
            if (success) {
                // Refresh groups list
                const groups = await getUserGroups(userId);
                setMyGroups(groups);
                // Clear selection if deleted group was selected
                if (selectedGroup?.id === deleteGroupModal.group.id) {
                    setSelectedGroup(null);
                    setGroupMembers([]);
                }
            }
        } catch (err) {
            console.error('Error deleting group:', err);
        }
        setDeleteGroupModal({ isOpen: false, group: null });
    };

    // Handle members added callback
    const handleMembersAdded = async (count) => {
        if (count > 0) {
            // Refresh groups list to update member count
            const groups = await getUserGroups(userId);
            setMyGroups(groups);
            // Refresh group members
            if (selectedGroup) {
                const groupDetails = await getGroupById(selectedGroup.groupUuid);
                if (groupDetails?.members) {
                    setGroupMembers(groupDetails.members);
                }
                // Update selected group with new data
                const updatedGroup = groups.find(g => g.id === selectedGroup.id);
                if (updatedGroup) {
                    setSelectedGroup(updatedGroup);
                }
            }
        }
    };

    // Handle remove contact from group
    const handleRemoveFromGroup = async (contactId, e) => {
        e.stopPropagation();
        if (!selectedGroup) return;

        try {
            await removeMemberFromGroup(selectedGroup.groupUuid, contactId);
            // Refresh group members
            const groupDetails = await getGroupById(selectedGroup.groupUuid);
            if (groupDetails?.members) {
                setGroupMembers(groupDetails.members);
            }
            // Refresh groups to update member count
            const groups = await getUserGroups(userId);
            setMyGroups(groups);
            const updatedGroup = groups.find(g => g.id === selectedGroup.id);
            if (updatedGroup) {
                setSelectedGroup(updatedGroup);
            }
        } catch (err) {
            console.error('Error removing from group:', err);
        }
    };

    // Convert contact to people-like object for PeopleCard
    // Contact data comes from Peoples table via JOIN on peopleuuid (LEFT JOIN)
    // contactType: 'PUBLIC' if peopleuuid is set, 'CONTACT' otherwise
    // Handle both camelCase and lowercase keys from API
    const contactToPerson = (contact) => ({
        id: contact.id,
        localuuid: contact.localuuid,
        peopleUuid: contact.peopleuuid || contact.peopleUuid,
        firstName: contact.firstName || contact.firstname,
        lastName: contact.lastName || contact.lastname,
        email: contact.email,
        phone: contact.phone,
        gsm: contact.gsm,
        company: contact.company,
        position: contact.position,
        googleAddress: contact.googleAddress || contact.googleaddress,
        latitude: contact.latitude,
        longitude: contact.longitude,
        website: contact.website,
        description: contact.description,
        contactType: contact.contactType || contact.contacttype || (contact.peopleuuid ? 'PUBLIC' : 'CONTACT'),
        role: contact.contactType || contact.contacttype || (contact.peopleuuid ? 'PUBLIC' : 'CONTACT'), // for backward compatibility
        // Profile type and employment status for PUBLIC contacts
        profileTypeId: contact.profileTypeId || contact.profiletypeid,
        profileTypeCode: contact.profileTypeCode || contact.profiletypecode,
        profileTypeName_fr: contact.profileTypeName_fr || contact.profiletypename_fr,
        profileTypeName_en: contact.profileTypeName_en || contact.profiletypename_en,
        employmentStatus: contact.employmentStatus || contact.employmentstatus,
        // Sync-related fields
        profileRole: contact.profileRole || '',
        profileDeleted: contact.profileDeleted || false
    });

    return (
        <div className="bg-white">
            {/* Page Header */}
            <PageHeader
                moduleCode="CONTACTS"
                title={t.contacts?.title || 'Contacts'}
                subtitle={t.contacts?.subtitle || 'Gérez vos contacts et groupes'}
            />

            {/* Groups Section */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Users className="w-5 h-5 text-cabaza-purple" />
                        {t.groups?.myGroups || 'My groups'}
                    </h3>
                    <Button onClick={() => setShowCreateGroupModal(true)} icon={Plus} size="sm">
                        {t.groups?.createGroup || 'Create group'}
                    </Button>
                </div>

                {myGroups.length === 0 ? (
                    <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                        {t.groups?.noGroups || 'No groups created. Create a group to organize your contacts!'}
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-3">
                        {myGroups.map((group, index) => {
                            const isSelected = selectedGroup?.id === group.id;
                            return (
                                <div
                                    key={group.id || group.groupUuid || index}
                                    onClick={() => handleGroupClick(group)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                                        isSelected
                                            ? 'bg-cabaza-purple text-white'
                                            : 'bg-cabaza-purple/10 text-cabaza-purple hover:bg-cabaza-purple/20'
                                    }`}
                                >
                                    <Users className="w-4 h-4" />
                                    <span className="font-medium">{getGroupName(group, language)}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        isSelected ? 'bg-white/20' : 'bg-cabaza-purple/20'
                                    }`}>
                                        {group.memberCount || group.members?.length || 0}
                                    </span>
                                    <button
                                        onClick={(e) => handleDeleteGroup(group, e)}
                                        className={`p-1 rounded-full transition-colors ${
                                            isSelected
                                                ? 'hover:bg-white/20 text-white'
                                                : 'hover:bg-red-100 text-red-500'
                                        }`}
                                        title={t.groups?.deleteGroup || 'Delete group'}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Divider */}
            <hr className="my-6 border-gray-200" />

            {/* Search and View Mode */}
            <div className="mb-8 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1 flex flex-wrap items-center gap-2 border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-cabaza-purple/20 focus-within:border-cabaza-purple transition-shadow bg-white px-3 py-2">
                    <Search className="text-gray-400 w-5 h-5 flex-shrink-0" />
                    {/* Group filter chip */}
                    {selectedGroup && (
                        <div className="flex items-center gap-1 bg-cabaza-purple text-white px-2 py-1 rounded text-sm flex-shrink-0">
                            <Users className="w-3 h-3" />
                            <span className="max-w-[80px] sm:max-w-[120px] truncate">{getGroupName(selectedGroup, language)}</span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearGroupFilter();
                                }}
                                className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t.dashboard?.searchInContacts || 'Rechercher dans mes contacts...'}
                        className="flex-1 min-w-[100px] py-1 bg-transparent border-none focus:outline-none focus:ring-0"
                    />
                </div>
                <div className="flex gap-2">
                    {/* Add member button - only visible when a group is selected */}
                    {selectedGroup && (
                        <button
                            onClick={handleAddMembersClick}
                            className="p-3 rounded-lg bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white hover:opacity-90 transition-opacity"
                            title={t.groups?.addMember || 'Add member'}
                        >
                            <UserPlus className="w-5 h-5" />
                        </button>
                    )}
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`p-3 rounded-lg transition-colors ${
                            viewMode === 'cards'
                                ? 'bg-cabaza-purple text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={t.dashboard?.cardView || 'Card view'}
                    >
                        <Grid className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-3 rounded-lg transition-colors ${
                            viewMode === 'list'
                                ? 'bg-cabaza-purple text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={t.dashboard?.listView || 'List view'}
                    >
                        <List className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Loading state */}
            {loading && (
                <div className="text-center py-12">
                    <div className="w-12 h-12 border-4 border-cabaza-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">
                        {t.dashboard?.loading || 'Loading...'}
                    </p>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center max-w-md mx-auto mb-8">
                    <p className="text-red-800 mb-4">
                        {t.profile?.loadingError || 'Loading error'}: {error}
                    </p>
                    <button
                        onClick={() => loadMyContacts()}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                    >
                        {t.dashboard?.retry || 'Retry'}
                    </button>
                </div>
            )}

            {/* Contacts display */}
            {!loading && !error && (
                <>
                    {filteredContacts.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <p className="text-gray-500 text-lg mb-4">
                                {myContacts.length === 0
                                    ? (t.dashboard?.noSavedContacts || 'No saved contacts. Go to "Search" to add contacts.')
                                    : (t.dashboard?.noContacts || 'No contacts found')}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Results count */}
                            <div className="mb-4 text-sm text-gray-500">
                                {filteredContacts.length} {t.dashboard?.contact || 'contact(s)'}
                                {totalPages > 1 && ` - ${t.dashboard?.page || 'Page'} ${currentPage}/${totalPages}`}
                            </div>

                            {/* Cards View */}
                            {viewMode === 'cards' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {currentContacts.map((contact) => (
                                        <PeopleCard
                                            key={contact.id}
                                            person={contactToPerson(contact)}
                                            onClick={() => handleContactClick(contact)}
                                            actionType="delete"
                                            onAction={(_, e) => handleRemoveContact(contact.id, e)}
                                            userEmail={userEmail}
                                            language={language}
                                            variant="dashboard"
                                            rating={contact.rating || 0}
                                            onRatingChange={(newRating) => handleRatingChange(contact.id, newRating)}
                                            onRemoveFromGroup={selectedGroup ? (_, e) => handleRemoveFromGroup(contact.id, e) : undefined}
                                            onEdit={handleEditContact}
                                            t={t}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* List View */}
                            {viewMode === 'list' && (
                                <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.favorites || 'Favorites'}
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.name || 'Name'}
                                                </th>
                                                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.company || 'Company'}
                                                </th>
                                                <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.phone || 'Phone'}
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.actions || 'Actions'}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentContacts.map((contact) => {
                                                const contactType = contact.contactType || (contact.peopleuuid ? 'PUBLIC' : 'CONTACT');
                                                const styles = getCardStyles(contactType, contact.email);

                                                return (
                                                    <tr
                                                        key={contact.id}
                                                        className="hover:bg-gray-50 transition-colors"
                                                    >
                                                        <td className="px-4 py-4">
                                                            <StarRating
                                                                rating={contact.rating || 0}
                                                                onRatingChange={(newRating) => handleRatingChange(contact.id, newRating)}
                                                                size="sm"
                                                            />
                                                        </td>
                                                        <td
                                                            onClick={() => handleContactClick(contact)}
                                                            className="px-6 py-4 cursor-pointer"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-sm font-medium ${styles.title}`}>
                                                                    {contact.firstName} {contact.lastName}
                                                                </span>
                                                                {contactType === 'PRIVATE' && (
                                                                    <span className="px-1.5 py-0.5 text-xs font-medium bg-orange-500 text-white rounded">
                                                                        PRIVATE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {contact.position && (
                                                                <div className="text-xs text-gray-500">{contact.position}</div>
                                                            )}
                                                        </td>
                                                        <td className="hidden md:table-cell px-6 py-4">
                                                            <div className="text-sm text-gray-600">{contact.company || '-'}</div>
                                                        </td>
                                                        <td className="hidden lg:table-cell px-6 py-4">
                                                            <div className="text-sm text-gray-600 truncate max-w-xs">{contact.email || '-'}</div>
                                                        </td>
                                                        <td className="hidden xl:table-cell px-6 py-4">
                                                            <div className="text-sm text-gray-600">{contact.phone || '-'}</div>
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <button
                                                                onClick={(e) => handleRemoveContact(contact.id, e)}
                                                                className="p-2 bg-red-100 hover:bg-red-200 rounded-lg text-red-600 transition-colors inline-flex items-center"
                                                                title={t.dashboard?.remove || 'Remove'}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-8 flex items-center justify-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>

                                    <div className="flex gap-2">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                                    currentPage === page
                                                        ? 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Debug Panel Component */}
            <DebugPanel
                localStorageKey="cabaza_user_contacts"
                serverFileName="contacts.json"
                fields={debugFields}
                loadFromFile={loadContactsFromFile}
                saveToFile={saveContactsToFile}
                onDataChange={loadMyContacts}
                setMainData={setMyContacts}
                title="Debug Contacts Panel"
            />

            {/* Delete Contact Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirmModal.isOpen}
                onClose={() => setDeleteConfirmModal({ isOpen: false, contact: null })}
                onConfirm={confirmDeleteContact}
                title={t.dashboard?.deleteContactTitle || 'Delete contact'}
                message={t.dashboard?.deleteContactWarning || 'Are you sure you want to delete this contact?'}
                confirmText={t.dashboard?.confirmDelete || 'Delete'}
                variant="danger"
            >
                <div className="bg-gray-100 rounded-lg p-3 mb-4">
                    <p className="font-semibold text-gray-900">
                        {deleteConfirmModal.contact?.firstName} {deleteConfirmModal.contact?.lastName}
                    </p>
                    {deleteConfirmModal.contact?.email && (
                        <p className="text-sm text-gray-600">{deleteConfirmModal.contact.email}</p>
                    )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-amber-800 font-medium text-sm">
                                {t.dashboard?.deleteContactInfo || 'This will permanently remove the contact from your entire contact system.'}
                            </p>
                            <p className="text-amber-700 text-sm mt-2">
                                {t.dashboard?.deleteContactTip || 'To remove a contact from a group only, use the'}
                                <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                                    <UserMinus className="w-3 h-3 mr-1" />
                                    {t.dashboard?.removeFromGroupIcon || 'remove from group'}
                                </span>
                                {t.dashboard?.deleteContactTipEnd || 'icon instead.'}
                            </p>
                        </div>
                    </div>
                </div>
            </ConfirmModal>

            {/* Delete Group Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteGroupModal.isOpen}
                onClose={() => setDeleteGroupModal({ isOpen: false, group: null })}
                onConfirm={confirmDeleteGroup}
                title={t.groups?.deleteGroupTitle || 'Delete group'}
                message={t.groups?.deleteGroupWarning || 'Are you sure you want to delete this group?'}
                confirmText={t.groups?.confirmDeleteGroup || 'Delete group'}
                icon={Users}
                variant="theme"
            >
                <div className="bg-gradient-to-r from-cabaza-purple/10 to-cabaza-pink/10 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-cabaza-purple/20 rounded-full">
                            <Users className="w-5 h-5 text-cabaza-purple" />
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900">
                                {getGroupName(deleteGroupModal.group, language)}
                            </p>
                            <p className="text-sm text-gray-600">
                                {(deleteGroupModal.group?.memberCount || 0)} {t.groups?.members || 'member(s)'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Info about members */}
                <div className={`rounded-lg p-4 ${
                    (deleteGroupModal.group?.memberCount || 0) > 0
                        ? 'bg-amber-50 border border-amber-200'
                        : 'bg-blue-50 border border-blue-200'
                }`}>
                    <div className="flex gap-3">
                        <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            (deleteGroupModal.group?.memberCount || 0) > 0
                                ? 'text-amber-600'
                                : 'text-blue-600'
                        }`} />
                        <div>
                            <p className={`font-medium text-sm ${
                                (deleteGroupModal.group?.memberCount || 0) > 0
                                    ? 'text-amber-800'
                                    : 'text-blue-800'
                            }`}>
                                {(deleteGroupModal.group?.memberCount || 0) > 0
                                    ? (t.groups?.deleteGroupInfoWithMembers || 'This group contains {count} member(s) who will be removed from the group.').replace('{count}', deleteGroupModal.group?.memberCount || 0)
                                    : (t.groups?.deleteGroupInfoNoMembers || 'This group has no members.')
                                }
                            </p>
                            <p className={`text-sm mt-2 ${
                                (deleteGroupModal.group?.memberCount || 0) > 0
                                    ? 'text-amber-700'
                                    : 'text-blue-700'
                            }`}>
                                {t.groups?.deleteGroupNote || 'Contacts will not be deleted from your list, they will only be removed from this group.'}
                            </p>
                        </div>
                    </div>
                </div>
            </ConfirmModal>

            {/* Create Group Modal */}
            <CreateGroupModal
                isOpen={showCreateGroupModal}
                onClose={() => setShowCreateGroupModal(false)}
                contacts={myContacts}
                userEmail={userEmail}
                userId={userId}
                onGroupCreated={handleGroupCreated}
            />

            {/* Add Members Modal */}
            <AddMembersModal
                isOpen={showAddMembersModal}
                onClose={() => setShowAddMembersModal(false)}
                group={selectedGroup}
                contacts={myContacts}
                onMembersAdded={handleMembersAdded}
            />

            {/* Edit Contact Modal */}
            {editContactModal.isOpen && editContactModal.contact && (
                <EditContactModal
                    isOpen={editContactModal.isOpen}
                    onClose={() => setEditContactModal({ isOpen: false, contact: null })}
                    contact={editContactModal.contact}
                    onContactUpdated={(updatedContact) => {
                        // Update contact in state
                        setMyContacts(prev => prev.map(c =>
                            c.id === updatedContact.id ? { ...c, ...updatedContact } : c
                        ));
                        setEditContactModal({ isOpen: false, contact: null });
                    }}
                    onContactDeleted={(deletedContact) => {
                        // Remove contact from state
                        setMyContacts(prev => prev.filter(c => c.id !== deletedContact.id));
                        setEditContactModal({ isOpen: false, contact: null });
                    }}
                />
            )}
        </div>
    );
};

export default MyContacts;
